import React, { useCallback, useEffect, useState } from 'react';
import { ChevronLeft, Loader2, Users, Clock, MapPin, User, Calendar, Pencil, Check } from 'lucide-react';
import { atlasApi, AtlasUser, Treinamento, TreinamentoInscricao } from '../shared/atlasApi';
import BadgeStatusT from './BadgeStatusT';

interface Props {
  user: AtlasUser;
  treinamentoId: string;
  podeEditar: boolean;
  onVoltar: () => void;
  onEditar: (t: Treinamento) => void;
}

function fdata(s?: string | null) {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; }
}

const STATUS_INSC = ['inscrito', 'presente', 'ausente', 'concluido', 'cancelado'];

const Detalhe: React.FC<Props> = ({ user, treinamentoId, podeEditar, onVoltar, onEditar }) => {
  const [tre, setTre] = useState<Treinamento | null>(null);
  const [inscritos, setInscritos] = useState<TreinamentoInscricao[]>([]);
  const [totalInscritos, setTotalInscritos] = useState(0);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [acao, setAcao] = useState(false);

  const carregar = useCallback(() => {
    setLoading(true);
    setErro(null);
    atlasApi.getTreinamento(user, treinamentoId)
      .then(r => { setTre(r.treinamento); setInscritos(r.inscritos || []); setTotalInscritos(r.total_inscritos || 0); })
      .catch(e => setErro(e.message || 'Erro ao carregar'))
      .finally(() => setLoading(false));
  }, [user, treinamentoId]);

  useEffect(() => { carregar(); }, [carregar]);

  const inscrever = async () => {
    setAcao(true);
    try { await atlasApi.inscreverTreinamento(user, treinamentoId); carregar(); }
    catch (e: any) { setErro(e.message || 'Erro ao inscrever'); }
    finally { setAcao(false); }
  };

  const mudarInscricao = async (inscId: string, status: string) => {
    try { await atlasApi.atualizarInscricao(user, treinamentoId, inscId, { status }); carregar(); }
    catch (e: any) { setErro(e.message || 'Erro'); }
  };

  if (loading) return <div className="p-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Carregando...</div>;
  if (erro && !tre) return <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-rose-700">{erro}</div>;
  if (!tre) return null;

  return (
    <div className="space-y-4 max-w-3xl">
      <button onClick={onVoltar} className="text-sm text-gray-500 hover:text-purple-600 flex items-center gap-1">
        <ChevronLeft className="w-4 h-4" /> Voltar
      </button>

      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900">{tre.titulo}</h2>
              <BadgeStatusT status={tre.status} />
              {tre.obrigatorio && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-700">Obrigatório</span>}
            </div>
            {tre.categoria && <p className="text-sm text-gray-400 mt-0.5">{tre.categoria} · {tre.modalidade}</p>}
          </div>
          {podeEditar && (
            <button onClick={() => onEditar(tre)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 flex items-center gap-2">
              <Pencil className="w-4 h-4" /> Editar
            </button>
          )}
        </div>

        {tre.descricao && <p className="text-sm text-gray-700 mt-3 whitespace-pre-wrap">{tre.descricao}</p>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
          <div className="flex items-center gap-2 text-gray-600"><Calendar className="w-4 h-4 text-gray-400" /> {fdata(tre.data_inicio)}</div>
          <div className="flex items-center gap-2 text-gray-600"><Clock className="w-4 h-4 text-gray-400" /> {tre.carga_horaria || '—'}h</div>
          <div className="flex items-center gap-2 text-gray-600"><User className="w-4 h-4 text-gray-400" /> {tre.instrutor || '—'}</div>
          <div className="flex items-center gap-2 text-gray-600"><MapPin className="w-4 h-4 text-gray-400" /> {tre.local || '—'}</div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <span className="text-sm text-gray-500 flex items-center gap-1"><Users className="w-4 h-4" /> {totalInscritos}{tre.vagas ? ` / ${tre.vagas}` : ''} inscritos</span>
          <button onClick={inscrever} disabled={acao} className="ml-auto px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-medium flex items-center gap-2">
            {acao ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Inscrever-me
          </button>
        </div>
      </div>

      {erro && <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700">{erro}</div>}

      {podeEditar && (
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100"><h3 className="font-bold text-gray-900 text-sm">Inscritos</h3></div>
          {inscritos.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">Ninguém inscrito ainda.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[11px] text-gray-500 uppercase tracking-wider">
                <tr><th className="px-4 py-2 text-left">Funccodi</th><th className="px-4 py-2 text-left">Status</th><th className="px-4 py-2 text-left">Nota</th></tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {inscritos.map(i => (
                  <tr key={i._id}>
                    <td className="px-4 py-2 text-gray-700">#{i.funccodi}</td>
                    <td className="px-4 py-2">
                      <select value={i.status} onChange={e => mudarInscricao(i._id, e.target.value)} className="px-2 py-1 rounded border border-gray-200 text-xs">
                        {STATUS_INSC.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="px-4 py-2 text-gray-600">{i.nota ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default Detalhe;
